import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { bondsInsurancePage, companyInfo } from '../mock';
import { CheckCircle2, Phone, Mail, Shield, ExternalLink, ArrowRight } from 'lucide-react';

const BondsInsurance = () => {
  const { language } = useLanguage();
  const content = bondsInsurancePage[language];

  // Enlace exclusivo confirmado de Propeller para Palerm Insurance Agency
  const bondsPortalUrl = 'https://FC44360.propeller.insure/axelerator-public';

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-indigo-700 to-indigo-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/10 p-4 rounded-full mb-6">
              <Shield className="w-16 h-16" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {content.title}
            </h1>
            <p className="text-xl md:text-2xl text-indigo-100 mb-8">
              {content.subtitle}
            </p>
            <p className="text-lg text-indigo-50 mb-10">
              {content.description}
            </p>
            
            {/* CTA Principal - Portal de Fianzas */}
            <a
              href={bondsPortalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white text-indigo-700 px-10 py-5 rounded-full text-xl font-bold hover:bg-indigo-50 transition-all shadow-2xl hover:shadow-indigo-500/50 hover:scale-105 transform"
            >
              <Shield className="w-7 h-7" />
              {language === 'en' ? 'Get a Bond Quote' : 'Cotizar y Solicitar una Fianza'}
              <ExternalLink className="w-6 h-6" />
            </a>
          </div>
        </div>
      </section>

      {/* Proceso de Solicitud - How It Works */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900">
              {language === 'en' ? 'How to Get Your Bond' : 'Cómo Obtener Su Fianza'}
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              {language === 'en' 
                ? 'Simple online process - no account or login required' 
                : 'Proceso simple en línea - no requiere crear cuenta ni iniciar sesión'}
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Step 1 */}
              <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-6 rounded-xl border-2 border-indigo-200 hover:border-indigo-400 transition-all">
                <div className="bg-indigo-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4">
                  1
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {language === 'en' ? 'Search Your Bond' : 'Buscar Su Fianza'}
                </h3>
                <p className="text-gray-600 text-sm">
                  {language === 'en' 
                    ? 'Find your bond by state or bond type in the portal' 
                    : 'Encuentre su fianza por estado o tipo de fianza en el portal'}
                </p>
              </div>

              {/* Step 2 */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border-2 border-blue-200 hover:border-blue-400 transition-all">
                <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4">
                  2
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {language === 'en' ? 'Complete Application' : 'Completar Solicitud'}
                </h3>
                <p className="text-gray-600 text-sm">
                  {language === 'en' 
                    ? 'Fill out the online application with required information' 
                    : 'Complete la solicitud en línea con la información requerida'}
                </p>
              </div>

              {/* Step 3 */}
              <div className="bg-gradient-to-br from-cyan-50 to-teal-50 p-6 rounded-xl border-2 border-cyan-200 hover:border-cyan-400 transition-all">
                <div className="bg-cyan-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4">
                  3
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {language === 'en' ? 'Review & Approval' : 'Revisión y Aprobación'}
                </h3>
                <p className="text-gray-600 text-sm">
                  {language === 'en' 
                    ? 'Your application will be reviewed when applicable' 
                    : 'Su solicitud será revisada cuando aplique'}
                </p>
              </div>

              {/* Step 4 */}
              <div className="bg-gradient-to-br from-teal-50 to-green-50 p-6 rounded-xl border-2 border-teal-200 hover:border-teal-400 transition-all">
                <div className="bg-teal-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4">
                  4
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {language === 'en' ? 'Payment & Delivery' : 'Pago y Entrega'}
                </h3>
                <p className="text-gray-600 text-sm">
                  {language === 'en' 
                    ? 'Complete payment and receive your bond following the portal instructions' 
                    : 'Complete el pago y reciba su fianza siguiendo las instrucciones del portal'}
                </p>
              </div>
            </div>

            {/* Important Note */}
            <div className="mt-8 bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong className="text-amber-800">
                  {language === 'en' ? 'Important:' : 'Importante:'}
                </strong>{' '}
                {language === 'en' 
                  ? 'Bond approval and processing times may vary depending on bond type and underwriting requirements. Not all bonds are approved instantly.' 
                  : 'Los tiempos de aprobación y procesamiento de fianzas pueden variar según el tipo de fianza y los requisitos de suscripción. No todas las fianzas se aprueban instantáneamente.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Educational Content - What is a Surety Bond */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900">
              {language === 'en' ? 'Understanding Surety Bonds' : 'Entendiendo las Fianzas'}
            </h2>
            <p className="text-center text-gray-600 mb-12">
              {language === 'en' 
                ? 'Learn about surety bonds and how they work' 
                : 'Aprenda sobre las fianzas y cómo funcionan'}
            </p>

            {/* What is a Surety Bond */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h3 className="text-2xl font-bold text-indigo-900 mb-4">
                {language === 'en' ? 'What is a Surety Bond?' : '¿Qué es una Fianza?'}
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                {language === 'en' 
                  ? 'A surety bond is a legally binding contract that serves as a guarantee that an obligation will be fulfilled to a third party. It is a three-party agreement that ensures compliance with laws, regulations, or contractual commitments.' 
                  : 'Una fianza es un contrato legalmente vinculante que sirve como garantía de que se cumplirá una obligación frente a un tercero. Es un acuerdo entre tres partes que asegura el cumplimiento de leyes, regulaciones o compromisos contractuales.'}
              </p>
              <p className="text-gray-700 leading-relaxed">
                {language === 'en' 
                  ? 'The bond protects the beneficiary according to the terms specified in the bond agreement. It provides financial assurance that obligations will be met as promised.' 
                  : 'La fianza protege al beneficiario según los términos especificados en el acuerdo de fianza. Proporciona seguridad financiera de que las obligaciones serán cumplidas como se prometió.'}
              </p>
            </div>

            {/* Three Parties Involved */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h3 className="text-2xl font-bold text-indigo-900 mb-6">
                {language === 'en' ? 'Who is Involved?' : '¿Quién Participa?'}
              </h3>
              <div className="space-y-6">
                {/* Principal */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">👤</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                      {language === 'en' ? 'Principal (Applicant)' : 'Principal (Solicitante)'}
                    </h4>
                    <p className="text-gray-700">
                      {language === 'en' 
                        ? 'The individual or business that purchases the bond and is required to fulfill the obligation. The principal may be obligated to reimburse the surety for any payments and expenses made under the bond, in accordance with the indemnity agreement.' 
                        : 'La persona o empresa que compra la fianza y está obligada a cumplir la obligación. El solicitante puede tener la obligación de reembolsar a la afianzadora cualquier pago y gasto realizado bajo la fianza, conforme al acuerdo de indemnización.'}
                    </p>
                  </div>
                </div>

                {/* Obligee */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">🏛️</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                      {language === 'en' ? 'Obligee (Beneficiary)' : 'Obligee (Beneficiario)'}
                    </h4>
                    <p className="text-gray-700">
                      {language === 'en' 
                        ? 'The party requiring the bond - typically a government agency, project owner, or entity that requires assurance the obligation will be fulfilled. The obligee is protected by the bond.' 
                        : 'La parte que requiere la fianza - típicamente una agencia gubernamental, dueño de proyecto o entidad que requiere asegurar que la obligación será cumplida. El beneficiario está protegido por la fianza.'}
                    </p>
                  </div>
                </div>

                {/* Surety */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">🛡️</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                      {language === 'en' ? 'Surety (Bonding Company)' : 'Surety (Afianzadora)'}
                    </h4>
                    <p className="text-gray-700">
                      {language === 'en' 
                        ? 'The insurance or bonding company that issues the bond and guarantees payment to the obligee if the principal fails to fulfill their obligation. The surety evaluates the risk and sets the bond premium.' 
                        : 'La compañía de seguros o fianzas que emite la fianza y garantiza el pago al beneficiario si el solicitante no cumple su obligación. La afianzadora evalúa el riesgo y establece la prima de la fianza.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bond Amount vs Premium */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 mb-8 border-2 border-indigo-200">
              <h3 className="text-2xl font-bold text-indigo-900 mb-4">
                {language === 'en' ? 'Bond Amount vs. Premium' : 'Monto de Fianza vs. Prima'}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">
                    {language === 'en' ? 'Bond Amount' : 'Monto de Fianza'}
                  </h4>
                  <p className="text-gray-700 text-sm">
                    {language === 'en' 
                      ? 'The maximum amount the surety will pay if a valid claim is made. This is typically set by the obligee or by law.' 
                      : 'La cantidad máxima que la afianzadora pagará si se presenta un reclamo válido. Esto generalmente lo establece el beneficiario o la ley.'}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">
                    {language === 'en' ? 'Premium (Cost)' : 'Prima (Costo)'}
                  </h4>
                  <p className="text-gray-700 text-sm">
                    {language === 'en' 
                      ? 'The fee you pay to purchase the bond - typically a percentage of the bond amount. Requirements and cost vary by bond type, state, and applicant evaluation.' 
                      : 'La tarifa que usted paga para comprar la fianza - típicamente un porcentaje del monto de la fianza. Los requisitos y el costo varían según el tipo de fianza, estado y evaluación del solicitante.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Bonds - Expanded */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900">
              {language === 'en' ? 'Types of Bonds We Offer' : 'Tipos de Fianzas que Ofrecemos'}
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              {language === 'en' 
                ? 'Understanding the different categories of surety and fidelity bonds' 
                : 'Entendiendo las diferentes categorías de fianzas y fidelity bonds'}
            </p>
            
            <div className="space-y-6">
              {/* Commercial Surety Bonds */}
              <div className="bg-white rounded-xl shadow-lg border-2 border-purple-200 overflow-hidden">
                <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6">
                  <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                    <span className="text-3xl">📋</span>
                    {language === 'en' ? 'Commercial Surety Bonds' : 'Fianzas Comerciales'}
                  </h3>
                  <p className="text-purple-100 mt-2">
                    {language === 'en' 
                      ? 'License & Permit Bonds' 
                      : 'Fianzas de Licencias y Permisos'}
                  </p>
                </div>
                <div className="p-6">
                  <p className="text-gray-700 mb-4">
                    {language === 'en' 
                      ? 'These bonds guarantee compliance with laws, regulations, and professional standards. Required by government agencies to legally operate a business, practice a profession, or perform specific activities.' 
                      : 'Estas fianzas garantizan el cumplimiento de leyes, regulaciones y estándares profesionales. Requeridas por agencias gubernamentales para operar legalmente un negocio, ejercer una profesión o realizar actividades específicas.'}
                  </p>
                  <div className="bg-purple-50 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-gray-900 mb-2">
                      {language === 'en' ? 'What it guarantees:' : 'Qué garantiza:'}
                    </p>
                    <p className="text-sm text-gray-700">
                      {language === 'en' 
                        ? 'Compliance with licensing requirements, consumer protection laws, and ethical business practices in your industry.' 
                        : 'Cumplimiento con requisitos de licencias, leyes de protección al consumidor y prácticas comerciales éticas en su industria.'}
                    </p>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    <strong>{language === 'en' ? 'Common Examples:' : 'Ejemplos Comunes:'}</strong>
                  </p>
                  <ul className="text-sm text-gray-600 space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span><strong>{language === 'en' ? 'Contractor License Bonds:' : 'Fianzas de Licencia de Contratista:'}</strong> {language === 'en' ? 'General contractors, electricians, plumbers requiring state licensure' : 'Contratistas generales, electricistas, plomeros que requieren licencia estatal'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span><strong>{language === 'en' ? 'Motor Vehicle Dealer Bonds:' : 'Fianzas de Concesionarios:'}</strong> {language === 'en' ? 'Auto dealerships selling, leasing, or brokering vehicles' : 'Concesionarios que venden, arriendan o intermedian vehículos'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span><strong>{language === 'en' ? 'Freight Broker Bonds:' : 'Fianzas de Agentes de Carga:'}</strong> {language === 'en' ? 'Transportation intermediaries arranging shipments' : 'Intermediarios de transporte que coordinan envíos'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span><strong>{language === 'en' ? 'Sales Tax Permit Bonds:' : 'Fianzas de Permisos de Impuestos:'}</strong> {language === 'en' ? 'Businesses collecting and remitting sales tax' : 'Negocios que recaudan y remiten impuestos sobre ventas'}</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Court & Fiduciary Bonds */}
              <div className="bg-white rounded-xl shadow-lg border-2 border-green-200 overflow-hidden">
                <div className="bg-gradient-to-r from-green-600 to-green-700 p-6">
                  <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                    <span className="text-3xl">⚖️</span>
                    {language === 'en' ? 'Court & Fiduciary Bonds' : 'Fianzas Judiciales y Fiduciarias'}
                  </h3>
                  <p className="text-green-100 mt-2">
                    {language === 'en' 
                      ? 'Judicial & Estate Administration Bonds' 
                      : 'Fianzas Judiciales y de Administración de Patrimonios'}
                  </p>
                </div>
                <div className="p-6">
                  <p className="text-gray-700 mb-4">
                    {language === 'en' 
                      ? 'Court-ordered bonds protecting beneficiaries when someone is appointed to manage property, assets, or fulfill duties under court supervision. These bonds ensure faithful performance of fiduciary responsibilities.' 
                      : 'Fianzas ordenadas por tribunales que protegen a beneficiarios cuando alguien es nombrado para administrar propiedades, activos o cumplir deberes bajo supervisión judicial. Estas fianzas aseguran el desempeño fiel de responsabilidades fiduciarias.'}
                  </p>
                  <div className="bg-green-50 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-gray-900 mb-2">
                      {language === 'en' ? 'What it guarantees:' : 'Qué garantiza:'}
                    </p>
                    <p className="text-sm text-gray-700">
                      {language === 'en' 
                        ? 'Proper management and distribution of estates, protection of minors and incapacitated persons, and faithful execution of court-appointed duties.' 
                        : 'Administración y distribución adecuada de patrimonios, protección de menores y personas incapacitadas, y ejecución fiel de deberes nombrados por el tribunal.'}
                    </p>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    <strong>{language === 'en' ? 'Common Examples:' : 'Ejemplos Comunes:'}</strong>
                  </p>
                  <ul className="text-sm text-gray-600 space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span><strong>{language === 'en' ? 'Probate/Executor Bonds:' : 'Fianzas de Albacea:'}</strong> {language === 'en' ? 'Executor administering a deceased person\'s estate' : 'Albacea administrando el patrimonio de una persona fallecida'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span><strong>{language === 'en' ? 'Guardianship Bonds:' : 'Fianzas de Tutela:'}</strong> {language === 'en' ? 'Guardian managing affairs of a minor or incapacitated adult' : 'Tutor administrando asuntos de un menor o adulto incapacitado'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span><strong>{language === 'en' ? 'Administrator Bonds:' : 'Fianzas de Administrador:'}</strong> {language === 'en' ? 'Court-appointed administrator of an intestate estate' : 'Administrador nombrado por tribunal de patrimonio sin testamento'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span><strong>{language === 'en' ? 'Conservatorship Bonds:' : 'Fianzas de Curatela:'}</strong> {language === 'en' ? 'Conservator managing financial affairs for protected person' : 'Curador administrando asuntos financieros de persona protegida'}</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Contract Surety Bonds */}
              <div className="bg-white rounded-xl shadow-lg border-2 border-orange-200 overflow-hidden">
                <div className="bg-gradient-to-r from-orange-600 to-orange-700 p-6">
                  <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                    <span className="text-3xl">🏗️</span>
                    {language === 'en' ? 'Contract Surety Bonds' : 'Fianzas de Contratos'}
                  </h3>
                  <p className="text-orange-100 mt-2">
                    {language === 'en' 
                      ? 'Construction & Project Performance Bonds' 
                      : 'Fianzas de Construcción y Cumplimiento de Proyectos'}
                  </p>
                </div>
                <div className="p-6 space-y-4">
                  {/* Bid Bond */}
                  <div className="border-l-4 border-orange-500 pl-4">
                    <h4 className="font-bold text-gray-900 mb-2">
                      {language === 'en' ? 'Bid Bond (Bid/Oferta)' : 'Fianza de Oferta (Bid)'}
                    </h4>
                    <p className="text-sm text-gray-700 mb-2">
                      {language === 'en' 
                        ? 'Guarantees that if your bid is accepted, you will sign the contract and provide required performance and payment bonds. Protects project owners from contractors who submit bids without serious intent.' 
                        : 'Garantiza que si su oferta es aceptada, firmará el contrato y proporcionará las fianzas de cumplimiento y pago requeridas. Protege a dueños de proyecto de contratistas que envían ofertas sin intención seria.'}
                    </p>
                    <p className="text-xs text-gray-600">
                      <strong>{language === 'en' ? 'Example:' : 'Ejemplo:'}</strong> {language === 'en' ? 'Public works project requiring all bidders to submit 10% bid bond' : 'Proyecto de obras públicas que requiere que todos los oferentes presenten fianza del 10%'}
                    </p>
                  </div>

                  {/* Performance Bond */}
                  <div className="border-l-4 border-orange-500 pl-4">
                    <h4 className="font-bold text-gray-900 mb-2">
                      {language === 'en' ? 'Performance Bond (Cumplimiento)' : 'Fianza de Cumplimiento (Performance)'}
                    </h4>
                    <p className="text-sm text-gray-700 mb-2">
                      {language === 'en' 
                        ? 'Ensures the contractor completes the project according to contract terms and specifications. If the contractor defaults, the surety steps in to complete the work or compensate the owner.' 
                        : 'Asegura que el contratista complete el proyecto según términos y especificaciones del contrato. Si el contratista incumple, la afianzadora interviene para completar el trabajo o compensar al dueño.'}
                    </p>
                    <p className="text-xs text-gray-600">
                      <strong>{language === 'en' ? 'Example:' : 'Ejemplo:'}</strong> {language === 'en' ? 'School construction requiring 100% performance bond for $5M project' : 'Construcción de escuela requiriendo fianza del 100% para proyecto de $5M'}
                    </p>
                  </div>

                  {/* Payment Bond */}
                  <div className="border-l-4 border-orange-500 pl-4">
                    <h4 className="font-bold text-gray-900 mb-2">
                      {language === 'en' ? 'Payment Bond (Pago)' : 'Fianza de Pago (Payment)'}
                    </h4>
                    <p className="text-sm text-gray-700 mb-2">
                      {language === 'en' 
                        ? 'Guarantees payment to subcontractors, laborers, and material suppliers. Protects those who provide labor and materials but have no direct contract with the project owner.' 
                        : 'Garantiza el pago a subcontratistas, trabajadores y proveedores de materiales. Protege a quienes proporcionan mano de obra y materiales pero no tienen contrato directo con el dueño del proyecto.'}
                    </p>
                    <p className="text-xs text-gray-600">
                      <strong>{language === 'en' ? 'Example:' : 'Ejemplo:'}</strong> {language === 'en' ? 'Federal project requiring payment bond to protect all subcontractors and suppliers' : 'Proyecto federal requiriendo fianza de pago para proteger a todos los subcontratistas y proveedores'}
                    </p>
                  </div>

                  {/* Maintenance Bond */}
                  <div className="border-l-4 border-orange-500 pl-4">
                    <h4 className="font-bold text-gray-900 mb-2">
                      {language === 'en' ? 'Maintenance Bond (Mantenimiento)' : 'Fianza de Mantenimiento (Maintenance)'}
                    </h4>
                    <p className="text-sm text-gray-700 mb-2">
                      {language === 'en' 
                        ? 'Covers defects in materials or workmanship for a specified period after project completion. Ensures the contractor will repair or replace defective work discovered during the warranty period.' 
                        : 'Cubre defectos en materiales o mano de obra por un período especificado después de completar el proyecto. Asegura que el contratista reparará o reemplazará trabajo defectuoso descubierto durante el período de garantía.'}
                    </p>
                    <p className="text-xs text-gray-600">
                      <strong>{language === 'en' ? 'Example:' : 'Ejemplo:'}</strong> {language === 'en' ? 'Roadway project with 2-year maintenance bond for pavement defects' : 'Proyecto de carretera con fianza de mantenimiento de 2 años para defectos del pavimento'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Fidelity Bonds */}
              <div className="bg-white rounded-xl shadow-lg border-2 border-blue-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
                  <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                    <span className="text-3xl">🛡️</span>
                    {language === 'en' ? 'Fidelity Bonds' : 'Fidelity Bonds (Deshonestidad)'}
                  </h3>
                  <p className="text-blue-100 mt-2">
                    {language === 'en' 
                      ? 'Employee Dishonesty Coverage' 
                      : 'Cobertura por Deshonestidad de Empleados'}
                  </p>
                </div>
                <div className="p-6">
                  <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
                    <p className="text-sm font-semibold text-yellow-800 mb-1">
                      {language === 'en' ? 'Important Distinction:' : 'Distinción Importante:'}
                    </p>
                    <p className="text-sm text-gray-700">
                      {language === 'en' 
                        ? 'Fidelity bonds are insurance policies, not surety bonds. They protect your business from financial loss, not a third party. The three-party surety relationship and indemnification agreement do not apply.' 
                        : 'Los fidelity bonds son pólizas de seguro, no fianzas. Protegen a su negocio de pérdidas financieras, no a un tercero. La relación tripartita de fianzas y el acuerdo de indemnización no aplican.'}
                    </p>
                  </div>
                  <p className="text-gray-700 mb-4">
                    {language === 'en' 
                      ? 'Insurance protection against losses caused by dishonest acts of employees or contractors. Coverage varies by policy form and may include theft, fraud, embezzlement, forgery, and other covered dishonest acts.' 
                      : 'Protección de seguro contra pérdidas causadas por actos deshonestos de empleados o contratistas. La cobertura varía según el formulario de póliza y puede incluir robo, fraude, malversación, falsificación y otros actos deshonestos cubiertos.'}
                  </p>
                  <div className="bg-blue-50 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-gray-900 mb-2">
                      {language === 'en' ? 'What it covers:' : 'Qué cubre:'}
                    </p>
                    <p className="text-sm text-gray-700">
                      {language === 'en' 
                        ? 'Financial losses from employee theft, embezzlement, fraud, or forgery. Depending on the policy form, may also cover resulting damage to client property.' 
                        : 'Pérdidas financieras por robo, malversación, fraude o falsificación de empleados. Dependiendo del formulario de póliza, también puede cubrir daños resultantes a propiedad del cliente.'}
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">
                    <strong>{language === 'en' ? 'Example:' : 'Ejemplo:'}</strong> {language === 'en' ? 'A bookkeeper embezzles $50,000 from company accounts over two years' : 'Un contador malversa $50,000 de cuentas de la compañía durante dos años'}
                  </p>
                </div>
              </div>

              {/* ERISA Bonds */}
              <div className="bg-white rounded-xl shadow-lg border-2 border-indigo-200 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 p-6">
                  <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                    <span className="text-3xl">📊</span>
                    {language === 'en' ? 'ERISA Bonds' : 'ERISA Bonds'}
                  </h3>
                  <p className="text-indigo-100 mt-2">
                    {language === 'en' 
                      ? 'Employee Benefit Plan Protection' 
                      : 'Protección de Planes de Beneficios de Empleados'}
                  </p>
                </div>
                <div className="p-6">
                  <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
                    <p className="text-sm font-semibold text-yellow-800 mb-1">
                      {language === 'en' ? 'Important Distinction:' : 'Distinción Importante:'}
                    </p>
                    <p className="text-sm text-gray-700">
                      {language === 'en' 
                        ? 'ERISA bonds are a specific type of fidelity bond (insurance), not surety bonds. Federal law requires them for pension and retirement plans. The three-party surety relationship does not apply.' 
                        : 'Los ERISA bonds son un tipo específico de fidelity bond (seguro), no fianzas. La ley federal los requiere para planes de pensión y jubilación. La relación tripartita de fianzas no aplica.'}
                    </p>
                  </div>
                  <p className="text-gray-700 mb-4">
                    {language === 'en' 
                      ? 'Federally-mandated insurance protecting employee benefit plans from losses due to fraud or dishonesty by those who handle plan funds. Required under the Employee Retirement Income Security Act for pension, 401(k), and other qualified retirement plans.' 
                      : 'Seguro federalmente obligatorio que protege planes de beneficios de empleados contra pérdidas por fraude o deshonestidad de quienes manejan fondos del plan. Requerido bajo la Ley de Seguridad de Ingresos de Jubilación de Empleados para planes de pensión, 401(k) y otros planes de jubilación calificados.'}
                  </p>
                  <div className="bg-indigo-50 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-gray-900 mb-2">
                      {language === 'en' ? 'What it covers:' : 'Qué cubre:'}
                    </p>
                    <p className="text-sm text-gray-700 mb-3">
                      {language === 'en' 
                        ? 'Losses to the plan caused by fraud or dishonesty of anyone who handles plan funds, including plan administrators, trustees, and custodians.' 
                        : 'Pérdidas al plan causadas por fraude o deshonestidad de cualquier persona que maneje fondos del plan, incluyendo administradores, fideicomisarios y custodios del plan.'}
                    </p>
                    <p className="text-xs text-gray-600">
                      <strong>{language === 'en' ? 'Minimum Coverage Required:' : 'Cobertura Mínima Requerida:'}</strong> {language === 'en' ? '10% of plan assets, typically capped at $500,000 (or $1M for certain plans)' : '10% de activos del plan, típicamente limitado a $500,000 (o $1M para ciertos planes)'}
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">
                    <strong>{language === 'en' ? 'Example:' : 'Ejemplo:'}</strong> {language === 'en' ? 'Company 401(k) plan with $3M in assets requires minimum $300,000 ERISA bond' : 'Plan 401(k) de compañía con $3M en activos requiere fianza ERISA mínima de $300,000'}
                  </p>
                </div>
              </div>
            </div>

            {/* Important Clarifications */}
            <div className="mt-10 space-y-4">
              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong className="text-red-800">
                    {language === 'en' ? 'Note:' : 'Nota:'}
                  </strong>{' '}
                  {language === 'en' 
                    ? 'Surety bonds are different from bail bonds (criminal bonds). This page refers to commercial and court surety bonds only.' 
                    : 'Las fianzas comerciales son diferentes de las fianzas penales/criminales. Esta página se refiere solo a fianzas comerciales y judiciales.'}
                </p>
              </div>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong className="text-blue-800">
                    {language === 'en' ? 'Availability:' : 'Disponibilidad:'}
                  </strong>{' '}
                  {language === 'en' 
                    ? 'Not all bond types are available in all states or situations. Bond availability depends on state regulations, bond type, and underwriting approval.' 
                    : 'No todos los tipos de fianzas están disponibles en todos los estados o situaciones. La disponibilidad depende de regulaciones estatales, tipo de fianza y aprobación de suscripción.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {language === 'en' ? 'Frequently Asked Questions' : 'Preguntas Frecuentes'}
            </h2>

            <div className="space-y-6">
              {/* FAQ 1 */}
              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-indigo-500">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {language === 'en' ? 'Which bond do I need?' : '¿Cuál fianza necesito?'}
                </h3>
                <p className="text-gray-700 mb-4">
                  {language === 'en' 
                    ? 'To determine which bond you need, gather the following information:' 
                    : 'Para determinar cuál fianza necesita, reúna la siguiente información:'}
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-1" />
                    <span><strong>{language === 'en' ? 'Requirement document or form:' : 'Documento o formulario del requisito:'}</strong> {language === 'en' ? 'The letter, form, or document stating the bond is required' : 'La carta, formulario o documento que indica que se requiere la fianza'}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-1" />
                    <span><strong>{language === 'en' ? 'State:' : 'Estado:'}</strong> {language === 'en' ? 'The state where the bond is required' : 'El estado donde se requiere la fianza'}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-1" />
                    <span><strong>{language === 'en' ? 'Bond amount:' : 'Monto de la fianza:'}</strong> {language === 'en' ? 'The required bond amount (if specified)' : 'El monto requerido de la fianza (si está especificado)'}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-1" />
                    <span><strong>{language === 'en' ? 'Obligee (entity):' : 'Obligee (entidad):'}</strong> {language === 'en' ? 'The government agency or entity requiring the bond' : 'La agencia gubernamental o entidad que requiere la fianza'}</span>
                  </li>
                </ul>
              </div>

              {/* FAQ 2 */}
              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {language === 'en' ? 'How much does a bond cost?' : '¿Cuánto cuesta una fianza?'}
                </h3>
                <p className="text-gray-700">
                  {language === 'en' 
                    ? 'The premium (cost) varies based on the bond type, required amount, your credit profile, financial strength, and business experience. Bond cost is subject to quotation and underwriting evaluation. Some bonds may require collateral or indemnitors depending on the risk assessment.' 
                    : 'La prima (costo) varía según el tipo de fianza, monto requerido, su perfil crediticio, solidez financiera y experiencia comercial. El costo de la fianza está sujeto a cotización y evaluación de suscripción. Algunas fianzas pueden requerir colateral o indemnizadores según la evaluación de riesgo.'}
                </p>
              </div>

              {/* FAQ 3 */}
              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-teal-500">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {language === 'en' ? 'What happens if there is a claim?' : '¿Qué pasa si hay un reclamo?'}
                </h3>
                <p className="text-gray-700">
                  {language === 'en' 
                    ? 'If the obligee files a valid claim against your bond, the surety will investigate. If the claim is valid, the surety may pay the claim up to the bond amount. However, unlike insurance, you (the principal) are ultimately responsible for reimbursing the surety for any payments and expenses made under the bond, according to the indemnity agreement you signed.' 
                    : 'Si el beneficiario presenta un reclamo válido contra su fianza, la afianzadora investigará. Si el reclamo es válido, la afianzadora puede pagar el reclamo hasta el monto de la fianza. Sin embargo, a diferencia del seguro, usted (el solicitante) es responsable en última instancia de reembolsar a la afianzadora cualquier pago y gasto realizado bajo la fianza, según el acuerdo de indemnización que firmó.'}
                </p>
              </div>
            </div>

            {/* Resources */}
            <div className="mt-10 bg-indigo-50 rounded-xl p-6 border-2 border-indigo-200">
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                {language === 'en' ? 'Additional Resources' : 'Recursos Adicionales'}
              </h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <a 
                    href="https://faq.propellerbonds.com/faq" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-indigo-700 hover:text-indigo-900 underline flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {language === 'en' ? 'Propeller Bonds FAQ' : 'Preguntas Frecuentes Propeller Bonds'}
                  </a>
                </li>
                <li>
                  <a 
                    href="https://agate.tdi.state.tx.us/commercial/pcbond.html" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-indigo-700 hover:text-indigo-900 underline flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {language === 'en' ? 'Texas Department of Insurance - Surety Bonds' : 'Departamento de Seguros de Texas - Fianzas'}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Examples */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {language === 'en' ? 'Types of Bonds' : 'Tipos de Fianzas'}
            </h2>
            <div className="grid gap-4">
              {content.coverageExamples.map((example, index) => (
                <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <CheckCircle2 className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-1" />
                  <p className="text-lg text-gray-700">{example}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What to Prepare */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {content.whatToPrepare.title}
            </h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
              <ul className="space-y-3">
                {content.whatToPrepare.items.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Need Help */}
      <section className="py-16 bg-gradient-to-r from-indigo-700 to-indigo-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {language === 'en' ? 'Need Help Getting Your Bond?' : '¿Necesita Ayuda con Su Fianza?'}
              </h2>
              <p className="text-xl text-indigo-100">
                {language === 'en' 
                  ? 'Our team is here to assist you through the bonding process' 
                  : 'Nuestro equipo está aquí para ayudarle con el proceso de fianzas'}
              </p>
            </div>

            {/* Contact Options */}
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {/* Phone */}
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-4 bg-white/10 hover:bg-white/20 p-6 rounded-xl transition-all backdrop-blur-sm border border-white/20"
              >
                <div className="bg-white/20 p-3 rounded-full">
                  <Phone className="w-8 h-8" />
                </div>
                <div className="text-left">
                  <p className="text-sm text-indigo-200 mb-1">
                    {language === 'en' ? 'Call Us' : 'Llámenos'}
                  </p>
                  <p className="text-xl font-bold">{companyInfo.phone}</p>
                </div>
              </a>

              {/* Email */}
              <a
                href={`mailto:${companyInfo.email}`}
                className="flex items-center gap-4 bg-white/10 hover:bg-white/20 p-6 rounded-xl transition-all backdrop-blur-sm border border-white/20"
              >
                <div className="bg-white/20 p-3 rounded-full">
                  <Mail className="w-8 h-8" />
                </div>
                <div className="text-left">
                  <p className="text-sm text-indigo-200 mb-1">
                    {language === 'en' ? 'Email Us' : 'Envíenos un Email'}
                  </p>
                  <p className="text-lg font-bold break-all">{companyInfo.email}</p>
                </div>
              </a>
            </div>

            {/* Primary CTA Button */}
            <div className="text-center">
              <a
                href={bondsPortalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-white text-indigo-700 px-10 py-5 rounded-full text-xl font-bold hover:bg-indigo-50 transition-all shadow-2xl hover:shadow-white/50 hover:scale-105 transform"
              >
                <Shield className="w-7 h-7" />
                {language === 'en' ? 'Get a Bond Quote' : 'Cotizar y Solicitar una Fianza'}
                <ExternalLink className="w-6 h-6" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BondsInsurance;